import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { isLoadChanger, setLoaderSuppressed } from "../../../store/mainSlice";
import { selectPartner } from "../../../store/partnerStore";
import { selectClientAddress } from "../../../store/userSlice";
import { resolvePartnerSync } from "../../../utils/partner";
import useUserLocation, { deriveUserCoords } from "../../hooks/useUserLocation";
import { isV2Enabled, V2_SCREEN_KEYS } from "../../registry";
import { commitGpsFix } from "../../utils/address";
import { GPS_FLOW_ENABLED, readGeolocationPermission, requestLocation } from "../../utils/resolveLocation";
import waitForLogin from "../../utils/waitForLogin";
import CategoryCarousel from "./CategoryCarousel";
import { WELCOME_CATEGORIES } from "./categories";
import useWelcomePrefetch from "./useWelcomePrefetch";
import WelcomeLayout from "./WelcomeLayout";
import "./welcome.scss";

// Below this, a wait isn't worth an overlay — it would paint and vanish.
const OVERLAY_DELAY_MS = 250;
// Once shown, the shortest it may stay, so it never reads as a flicker.
const OVERLAY_MIN_MS = 400;
// How long the CTA will wait on an in-flight Login before deciding without it. A
// hanging request must not strand the user on this screen; past this we fall back to
// the behaviour this screen had before it waited at all.
const LOGIN_WAIT_CAP_MS = 8000;

// V2 welcome screen, routed at /welcome. Shown once, at the end of the user's very
// first onboarding: the OTP → registration → questionnaire chain ends with an
// explicit navigate here, and nothing else in the app does — which is what makes it
// a one-time screen without any "seen" flag. Every other entry lands on home.
//
// It is also the app's location gate: the CTA below is a new user's first-ever
// permission prompt (home's entry refresh only runs once the handshake Token is
// in, which never happens before the OTP redirect — so it can't get there first).
// While the user reads the intro it warms the store for home (useWelcomePrefetch).
function Welcome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const { coords } = useUserLocation();
  // finishWelcome can now outlive this screen (it waits on Login), so it has to know
  // whether it still owns the route before navigating.
  const mountedRef = useRef(true);
  const clientAddress = useSelector(selectClientAddress);
  const mainUserData = useSelector((state) => state.userSlice.mainUserData);
  // resolvePartnerSync, not the bare selector: this screen can render mid-handshake,
  // before silentLogin has dispatched setPartner.
  const partner = resolvePartnerSync(useSelector(selectPartner));

  useWelcomePrefetch();

  // This screen is complete without the handshake, so the global overlay has no
  // business covering it: silentLogin's two round trips and the prefetch all run
  // behind a fully rendered intro. The only wait worth showing is the permission
  // dialog, which finishWelcome lifts the suppression for.
  useEffect(() => {
    dispatch(setLoaderSuppressed(true));
    return () => {
      mountedRef.current = false;
      dispatch(setLoaderSuppressed(false));
    };
  }, [dispatch]);

  const finishWelcome = async () => {
    // GPS flow disabled: the intro still shows, but it is no longer a location
    // gate — no prompt, no /location-required, straight to home.
    if (!GPS_FLOW_ENABLED) {
      navigate("/home", { replace: true });
      return;
    }

    let shownAt = null;
    const raiseOverlay = () => {
      shownAt = Date.now();
      // Lift this screen's veto first — the overlay is suppressed for the whole of
      // welcome, and this one wait is the user's own doing, so it earns an exception.
      dispatch(setLoaderSuppressed(false));
      dispatch(isLoadChanger(true));
    };

    const permission = await readGeolocationPermission();

    // Fast path: permission already granted and a location source already on file
    // (saved or profile address, via the same ladder home reads). The destination
    // is /home no matter what the fix says, so don't make the user watch a
    // multi-second GPS acquisition — resolve it in the background and let home's
    // >100m revalidate swap the rows in place when it lands. "granted" only: on
    // "prompt"/"unknown" getCurrentPosition may raise the permission dialog, which
    // must stay on this screen. The continuation only dispatches — it never
    // navigates, so it can't stomp a silentLogin redirect after this screen is gone.
    if (permission === "granted") {
      const { coords: fallbackCoords } = deriveUserCoords(store.getState().userSlice);
      if (fallbackCoords) {
        requestLocation().then((result) => {
          if (!result.coords) return;
          // Live values, not the tap-time closure: Login may land during the fix.
          const { clientAddress: liveAddress, mainUserData: liveUserData } = store.getState().userSlice;
          commitGpsFix({
            coords: result.coords,
            clientAddress: liveAddress,
            mainUserData: liveUserData,
            dispatch,
          });
        });
        navigate("/home", { replace: true });
        return;
      }
    }

    // A dialog we're waiting on is a certain wait, so show it straight away.
    // Otherwise let elapsed time decide: "granted" does not mean fast — it still does a
    // real fix, taking seconds, whenever nothing is cached inside requestLocation's
    // maximumAge — but when it *is* cached it returns in about a frame, and an overlay
    // that paints for one frame reads as a glitch rather than as feedback.
    let overlayTimer = null;
    if (permission === "prompt") raiseOverlay();
    else overlayTimer = setTimeout(raiseOverlay, OVERLAY_DELAY_MS);

    const result = await requestLocation();

    // Only when GPS gave us nothing does the answer still depend on Login: rung 3 of
    // the coordinate ladder is the profile address, which arrives with that response.
    // Deciding before it lands sent users with an address on file to the explainer they
    // didn't need. With coords in hand the destination is /home either way, so the happy
    // path never waits. The overlay is still up, so this wait is covered.
    if (!result.coords) await waitForLogin(store, LOGIN_WAIT_CAP_MS);

    clearTimeout(overlayTimer);

    // Once shown, hold it long enough to read. Without this the glitch just moves to the
    // boundary: a wait a hair over the delay would raise the overlay and drop it a frame
    // later. Held here rather than after navigating, so the wait is spent on this screen
    // instead of flashing over home.
    if (shownAt !== null) {
      const remaining = OVERLAY_MIN_MS - (Date.now() - shownAt);
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
      // Only what we raised: silentLogin may still own isLoad mid-handshake, and two
      // owners fighting over one boolean is what suppressLoader exists to avoid.
      dispatch(isLoadChanger(false));
    }

    if (result.coords) {
      // Not awaited: the coordinates land in the store synchronously, and turning
      // them into an address is a Google round trip plus a backend write. Home shows
      // the profile address meanwhile and refines to this when it lands.
      commitGpsFix({ coords: result.coords, clientAddress, mainUserData, dispatch });
    }

    // Waiting above means silentLogin may have routed somewhere itself in the meantime
    // — /registration for an empty email, /venue for a deep link. If it did, this screen
    // is gone and its decision is stale; don't overwrite theirs.
    if (!mountedRef.current) return;

    // Recomputed from the live store, not from this handler's closure: `coords` was read
    // when the tap happened, before Login could have filled in the profile address.
    const { coords: knownCoords } = deriveUserCoords(store.getState().userSlice);

    // Refused, and nothing on file to fall back on — the only case that needs the
    // explainer. A user whose profile or saved address already gives us coords is
    // never asked twice. `replace`: neither the intro nor the gate should sit in
    // history behind the home page.
    if (!result.coords && !knownCoords) {
      navigate("/location-required", { replace: true });
      return;
    }

    // No refetch here: if the fix differs from the coordinates the prefetch used,
    // home's own effect revalidates and swaps the rows in place.
    navigate("/home", { replace: true });
  };

  // Direct URL hit by a partner outside the rollout → home.
  if (!isV2Enabled(V2_SCREEN_KEYS.WELCOME, partner)) return <Navigate to="/" replace />;

  return (
    <WelcomeLayout ctaLabel={t("WELCOME_CTA")} onCta={finishWelcome}>
      <div className="wlc-v2">
        <h1 className="wlc-v2-title">{t("WELCOME_TITLE")}</h1>
        <p className="wlc-v2-text">{t("WELCOME_BODY")}</p>
      </div>
      <CategoryCarousel items={WELCOME_CATEGORIES} />
    </WelcomeLayout>
  );
}

export default Welcome;
